'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Calculator, ArrowRight } from 'lucide-react';

export const TTOSROICalculatorV3: React.FC = () => {
  const [weeklyHours, setWeeklyHours] = useState<number>(18);
  const [monthlyAgencySpend, setMonthlyAgencySpend] = useState<number>(35000);

  // Math:
  // TT OS automates ~85% of manual marketing hours
  const hoursSavedPerMonth = Math.round(weeklyHours * 4 * 0.85);
  // TT OS replaces traditional agency fees
  const moneySavedPerYear = Math.round((monthlyAgencySpend * 12) - 25000);

  return (
    <section className="py-20 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-2 mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
            <Calculator className="w-3.5 h-3.5" />
            <span>Interactive ROI Calculator</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            How Much Time &amp; Money Will TT OS Save You?
          </h2>
          <p className="text-sm text-slate-500">
            Slide the bars below to calculate your estimated annual operational savings.
          </p>
        </div>

        {/* 2-Column Calculator Box */}
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          
          {/* Sliders */}
          <div className="space-y-6">
            {/* Hours Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                <span>Hours spent on marketing &amp; social media / week:</span>
                <span className="px-2.5 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-extrabold text-sm">
                  {weeklyHours} Hours
                </span>
              </div>
              <input
                type="range"
                min="5"
                max="40"
                step="1"
                value={weeklyHours}
                onChange={(e) => setWeeklyHours(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>5 hrs (Part-time)</span>
                <span>20 hrs (Heavy)</span>
                <span>40 hrs (Full-time team)</span>
              </div>
            </div>

            {/* Spend Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                <span>Current monthly agency / freelance spend:</span>
                <span className="px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-extrabold text-sm">
                  ₹{monthlyAgencySpend.toLocaleString('en-IN')} / mo
                </span>
              </div>
              <input
                type="range"
                min="10000"
                max="150000"
                step="5000"
                value={monthlyAgencySpend}
                onChange={(e) => setMonthlyAgencySpend(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>₹10,000 / mo</span>
                <span>₹75,000 / mo</span>
                <span>₹1,50,000 / mo</span>
              </div>
            </div>
          </div>

          {/* Savings Result Card */}
          <div className="bg-[#0B0F19] text-white p-6 sm:p-7 rounded-2xl border border-slate-800 space-y-5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/15 blur-2xl rounded-full" />

            <div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Estimated Annual Savings:
              </span>
              <div className="text-3xl sm:text-4xl font-black text-emerald-400 tracking-tight mt-1">
                ₹{moneySavedPerYear > 0 ? moneySavedPerYear.toLocaleString('en-IN') : '0'}
                <span className="text-xs text-slate-400 font-normal"> / year</span>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-800 grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-slate-400 text-[11px] block">Time Reclaimed:</span>
                <span className="text-lg font-bold text-sky-400">{hoursSavedPerMonth} Hours</span>
                <span className="text-[10px] text-slate-500 block">every single month</span>
              </div>
              <div>
                <span className="text-slate-400 text-[11px] block">Execution Speed:</span>
                <span className="text-lg font-bold text-purple-400">10x Faster</span>
                <span className="text-[10px] text-slate-500 block">vs manual workflows</span>
              </div>
            </div>

            <Link
              href="/register"
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-md"
            >
              <span>Reclaim Your Time with TT OS</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

        </div>

      </div>
    </section>
  );
};

export default TTOSROICalculatorV3;
